import { legacy_connect as connect } from 'react-redux';
import * as core from 'cw-ui-core';
import App from './App';
import {
  checkIfEmailVerified,
  getPartnerAccountRequiredFields,
  isRequiredFieldsMissingSaved,
  lookupCompany
} from 'src/services/Partners/PartnersService';
import {
  getUserSecurity,
  userHasNoAccess,
  getSearchToken,
  getApplicationRoles,
  shouldHideNewsPage
} from 'src/services/User/UserService';
import { APP_PREREQUISITES_DIALOG } from 'src/DialogFactory';
import HasAccess from 'src/services/RoleAccess';
import Routes from 'src/services/Routes';
import AccessDeniedPage from 'src/components/AccessDeniedPage/AccessDeniedPage';
import { POLICIES } from 'src/services/Authorization';
import { getAllFeatures, getFeatures } from 'src/services/Features/FeaturesService';
import {
  getProducts,
  getPartnerProductsPurchased
} from 'src/services/Products/ProductsService';
import { checkEligibility } from 'src/services/AsioService';
import { getPublishedBanners } from 'src/services/Banner/BannerService';
import UserProfileService from 'src/services/Api/UserManagementApi/UserProfileService';
import { isChildAccount, isParentAccount } from 'src/components/UserAccount/SwitchAccount';
import { APP_ROLES, ROUTE_IDS } from 'src/constants/Constants';

let isPendoInitialized = false;

const initialize = async () => {
  await getInActivityTime();
  await checkEligibility();
  await getApplicationRoles('platform');
  await isChildAccount();
  await isParentAccount();
  await getApplicationRoles('connectwise.university'); // To get the application roles for ConnectWise University.
  await lookupCompany();
  await getSearchToken();
  await getAllFeatures();
  await getAndDispatchFeatures();
  await getProducts();
};

const checkPartnerAccountRequiredFields = async () => {
  const dispatch = core.Store().dispatch;
  if (HasAccess([POLICIES.ADMIN, POLICIES.BILLING_ADMIN], null)) {
    const hasRequiredFields = await getPartnerAccountRequiredFields();
    if (hasRequiredFields) {
      dispatch(
        core.showDialog(APP_PREREQUISITES_DIALOG, {
          callback: initialize
        })
      );
      return;
    }
  } else {
    await getPartnerAccountRequiredFields();
  }

  await initialize();
};

const getInActivityTime = async () => {
  const dispatch = core.Store().dispatch;
  var result = await UserProfileService.getInactivityTime();
  if (result.isSuccess) {
    dispatch(core.setModuleCache(['inactivityDuration'], result.response.inActivityTiming));
    dispatch(core.setModuleCache(['timeDuration'], result.response.timerTiming));
  }
};

const getAndDispatchFeatures = async () => {
  const dispatch = core.Store().dispatch;
  const features = await getFeatures();
  const publishedBanners = await getPublishedBanners();
  let featuresObj;
  if (features) {
    featuresObj = features.reduce((acc, item) => {
      return {
        ...acc,
        [item.featureId]: item
      };
    }, {});
  }

  dispatch(core.setModuleCache(['features'], featuresObj));
  dispatch(core.setModuleCache(['banners'], publishedBanners));
};

const mapStateToProps = state => {
  const partnerProducts = getPartnerProductsPurchased();

  const showNowIconInMenu =
    partnerProducts.filter(
      product =>
        product.productIdentifier === 'manage' ||
        product.productIdentifier === 'automate' ||
        product.productIdentifier === 'sell' ||
        product.productIdentifier === 'control'
    ).length > 0;

  const showMenu = core.getScreenCustom(state.screen, ['showMenu']);
  const emailConfirmed = core.getModuleCache(state.module, [
    'user',
    'emailConfirmed'
  ]);
  let haveRequiredFields = true; // CWH-487 - Assume the required fields exist for all users.
  if (HasAccess([POLICIES.ADMIN, POLICIES.BILLING_ADMIN], null)) {
    haveRequiredFields = isRequiredFieldsMissingSaved();
  }

  const featuresChk = core.getModuleCache(state.module, ['features']);
  const isReady = haveRequiredFields && emailConfirmed && featuresChk;
  const fullName = core.getUserName(state.oidc);
  const partnerModule = core.getModuleCache(state.module, ['partner']);
  const partner = partnerModule ? partnerModule.toJS() : undefined;
  const company = partner ? partner.companyName : undefined;
  const partnerId = core.getPartnerId(state.oidc);
  const email = core.getUserEmail(state.oidc);
  const ssoUserID = core.getUserId(state.oidc);
  const partnerCountry = partner ? partner.country : undefined;
  const partnerState = partner ? partner.state : undefined;
  const zip = partner ? partner.zip : undefined;

  const hasSuccessManager = core.getModuleCache(state.module, [
    'successManager'
  ])
    ? true
    : false;

  const appRoles = core.getUser(state.oidc).toJS()?.profile?.app_role_assignments || [];
  const userAppRoles = Array.isArray(appRoles) ? appRoles : [appRoles];
  const roles = userAppRoles.map(role => role.appRoleId);
  let isAdmin = false;
  let isUserManagement = false;
  let isBillingAdmin = false;
  let noSSORole = false;
  let manageSecurityRole = '';
  if (roles) {
    isAdmin = roles.includes(POLICIES.ADMIN);
    isUserManagement = roles.includes(POLICIES.USER_MANAGEMENT);
    isBillingAdmin = roles.includes(POLICIES.BILLING_ADMIN);
    noSSORole = !(isAdmin || isUserManagement || isBillingAdmin);
    manageSecurityRole = roles.filter(role => {
      if (
        !(
          role === POLICIES.ADMIN ||
          role === POLICIES.USER_MANAGEMENT ||
          role === POLICIES.BILLING_ADMIN
        )
      )
        return role;
      else return null;
    });
  }

  const productsCheck = core.getModuleCache(state.module, ['products']);
  const products = productsCheck ? productsCheck.toJS() : null;
  let accessToManage = false;
  let accessToAutomate = false;
  let accessToSell = false;
  let accessToControl = false;
  let accessToUnite = false;
  let accessToIdentify = false;
  if (products) {
    const productIdentifiers = products.map(product => {
      return product.productIdentifier;
    });
    accessToManage = productIdentifiers.includes('manage');
    accessToAutomate = productIdentifiers.includes('automate');
    accessToSell = productIdentifiers.includes('sell');
    accessToControl = productIdentifiers.includes('control');
    accessToUnite = productIdentifiers.includes('unite');
    accessToIdentify = productIdentifiers.includes('identify');
  }

  const canInitializePendo =
    !isPendoInitialized &&
    partnerModule &&
    productsCheck &&
    partnerId &&
    ssoUserID;

  if (
    canInitializePendo &&
    typeof window !== 'undefined' &&
    window.pendo &&
    typeof window.pendo.initialize === 'function'
  ) {
    window.pendo.initialize({
      visitor: {
        id: ssoUserID, // Required if user is logged in, otherwise default is 'Visitor'
        email,
        full_name: fullName,
        isAdmin,
        isUserManagement,
        isBillingAdmin,
        noSSORole,
        manageSecurityRole,
        hasSuccessManager,
        accessToManage,
        accessToAutomate,
        accessToSell,
        accessToControl,
        accessToUnite,
        accessToIdentify
      },
      account: {
        id: partnerId, // Required if using Pendo Feedback, otherwise default is 'Account'
        name: company,
        country: partnerCountry,
        state: partnerState,
        zip
      }
    });
    isPendoInitialized = true;
  }

  const configuredRoutes = () =>
    Object.keys(Routes).filter(
      key => !Routes[key].configured || Routes[key].configured()
    );

  const isRouteAllowed = route =>
    HasAccess(
      route.allowedRoles && route.allowedRoles(),
      route.disallowedRoles && route.disallowedRoles()
    ) && HasAccess(route.mustHaveRoles && route.mustHaveRoles());

  const getRouteList = () => {
    let routeList = configuredRoutes().map(key => {
      const route = Routes[key];
      if (isRouteAllowed(route)) {
        if (key === 'NOW_PAGE') {
          route.showInMenu = showNowIconInMenu;
        }

        return route;
      }

      return {
        ...route,
        component: AccessDeniedPage,
        showInMenu: false
      };
    });

    const state = core.Store().getState();
    const connectedAppRoles = core.getModuleCache(state.module, ['connectwise.university', 'app_role_assignments'])?.toJS();
    const hasUniversityStandardRole = connectedAppRoles.some(
      role => role.appRoleId === APP_ROLES.UNIVERSITY_STANDARD_USER
    );
    const hasTrainYourTeamRole = connectedAppRoles.some(
      role => role.appRoleId === APP_ROLES.TRAIN_YOUR_TEAM
    );
    // If the user doesn't have the University Standard role and Train Your Team, remove the university page from the route list.
    if (!hasUniversityStandardRole && !hasTrainYourTeamRole) {
      routeList = routeList.filter(item => item.id !== ROUTE_IDS.UNIVERSITY_PAGE);
    }
    
    const user = core.getUser(state.oidc);
    const isGuestUserLoggedIn = !!user.toJS()?.profile?.origin_partner_id;
    if (isGuestUserLoggedIn) {
        routeList = routeList.filter(
        item => item.id !== 'PartnerSupportRedirect' && item.id !== ROUTE_IDS.UNIVERSITY_PAGE
      );
    }

    if (shouldHideNewsPage({ user, state, core })) {
      routeList = routeList.filter(
        item => item.id !== 'news_page');
    }

    return routeList;
  };

  const isChild = core.getModuleCache(state.module, ['isChildAccount']);
  const isParent = core.getModuleCache(state.module, ['isParentAccount']);
  const user = core.getUser(state.oidc);
  const isGuestUserLoggedIn = !!user.toJS()?.profile?.origin_partner_id;

  return {
    showMenu: showMenu === undefined ? true : showMenu,
    isAppReady: isReady ? true : false,
    routeList: isReady ? getRouteList() : {},
    isChild,
    isParent,
    isGuestUserLoggedIn
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onDidMount: async () => {
      dispatch(core.setScreenCustom(['showMenu'], true));
      const userSecurity = await getUserSecurity();
      if (!userSecurity) {
        return;
      }

      if (userHasNoAccess()) {
        return;
      }

      const emailVerified = await checkIfEmailVerified();
      if (!emailVerified) {
        dispatch(
          core.showDialog('EMAIL_VERIFICATION', {
            callback: checkPartnerAccountRequiredFields
          })
        );
        return;
      }

      await checkPartnerAccountRequiredFields();
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
